import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge for conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Date utilities
export * from './date-helpers';

// Health Alerts utilities
export * from './health-alerts';

// Health Expenses utilities
export * from './health-expenses';

// Vaccination utilities
export * from './vaccination-helpers';

/**
 * Simple delay function for async operations
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Format date to locale string
 */
export function formatDate(date: Date | string, locale = 'pt-BR'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

/**
 * Format currency (BRL)
 */
export function formatCurrency(value: number, locale = 'pt-BR'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Format dog age from birth date
 * Returns human-readable age string in Portuguese
 *
 * @param birthDate - ISO date string or Date object
 * @returns Formatted age string (e.g., "2 anos e 3 meses", "8 meses", "15 dias")
 */
export function formatDogAge(birthDate: Date | string): string {
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
  const now = new Date();

  // Calculate difference in milliseconds
  const diffMs = now.getTime() - birth.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Less than 1 month: show days
  if (diffDays < 30) {
    return diffDays === 1 ? '1 dia' : `${diffDays} dias`;
  }

  // Calculate years and months
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();

  // Adjust for day of month
  if (now.getDate() < birth.getDate()) {
    months--;
  }

  // Adjust years if months is negative
  if (months < 0) {
    years--;
    months += 12;
  }

  // Less than 1 year: show only months
  if (years === 0) {
    return months === 1 ? '1 mês' : `${months} meses`;
  }

  // 1+ years: show years and months
  const yearStr = years === 1 ? '1 ano' : `${years} anos`;

  if (months === 0) {
    return yearStr;
  }

  const monthStr = months === 1 ? '1 mês' : `${months} meses`;
  return `${yearStr} e ${monthStr}`;
}
